// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Accounting period review route.
 *
 * Provides a page for reviewing accounting periods and their close run data.
 */
import { PeriodReviewPage } from '@/accounting/components/period-review-page';
import { PageContent } from '@/components/page-content';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

const searchSchema = z.object({
  period: z.string().optional(),
});

export const Route = createFileRoute('/_app/accounting/period-review')({
  component: AccountingPeriodReviewRoute,
  validateSearch: searchSchema,
});

/**
 * Parses a period key string (e.g., "2024-11") into year and month.
 * Returns undefined if the key is invalid.
 */
function parsePeriodKey(
  key: string | undefined
): { year: number; month: number } | undefined {
  if (!key) return undefined;
  const parts = key.split('-');
  if (parts.length !== 2) return undefined;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  if (isNaN(year) || isNaN(month)) return undefined;
  return { year, month };
}

function AccountingPeriodReviewRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const initialPeriodFromUrl = parsePeriodKey(search.period);

  /**
   * Updates the URL search params when the period changes.
   */
  const handlePeriodUrlChange = (periodKey: string) => {
    navigate({ search: (prev) => ({ ...prev, period: periodKey }) });
  };

  return (
    <PageContent
      sx={{
        gap: 3,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PeriodReviewPage
        initialPeriodFromUrl={initialPeriodFromUrl}
        onPeriodUrlChange={handlePeriodUrlChange}
      />
    </PageContent>
  );
}
